<script setup>
import dayjs from 'dayjs'
import { ref, shallowRef, computed, onMounted } from 'vue'
import { fixedDecimal, formatShortDate } from '@/lib/utils'

import { getI18n } from '@/plugins/i18n'
import router, { menuRouteEnum, menuRoutes } from '@/router'

import { DxDataGrid } from 'devextreme-vue/data-grid'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useCostReportRowColumns, useCostReportRowLabels, useCostReportRowTypes } from '@/composables/cost-report'
import { useBudgetDocumentRowLabels } from '@/composables/budget-document-row'
import { dueDateFrom, invoiceLineLabel, isValidPaymentDays, paymentDaysLabel, useInvoiceLines } from '@/composables/invoice'
import { usePriceService } from '@/composables/price.service'
import { useCurrencyColumnFormatter, useDefaultColumnChooser, useDefaultStateStoring } from '@/composables/data-grid'
import { useIsUpdated } from '@/composables/object'
import { useEscapeClose } from '@/composables/keyboard'
import { useRowNumberColumn, useUnitOfMeasureColumn, usePriceColumn, useCostColumn, useVatTypeColumn, useNotesColumn, useProjectColumn, useProjectsColumn } from '@/composables/data-grid-column'
import { useStepper } from '@/composables/stepper'
import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import { useNavigator } from '@/composables/navigator'

import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import DeleteOptionsDialog from '@/components/shared/DeleteOptionsDialog.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import CompanyCombobox from '@/components/shared/CompanyCombobox.vue'
import CurrencySelect from '@/components/shared/CurrencySelect.vue'
import FormSectionTitle from '@/components/shared/FormSectionTitle.vue'
import PrintTemplateSelect from '@/components/accounting/PrintTemplateSelect.vue'
import DocumentLanguageSelect from '@/components/shared/DocumentLanguageSelect.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import { infoBoxToneEnum, infoBoxIdEnum, usePortalStore } from '@/stores/portal.store'
import DocumentTermTemplateSelect from '@/components/accounting/DocumentTermTemplateSelect.vue'
import InvoiceLineEdit from '@/components/accounting/InvoiceLineEdit.vue'
import BudgetDocumentRowListItem from '@/components/accounting/BudgetDocumentRowListItem.vue'
import CostReportRowListItem from '@/components/accounting/CostReportRowListItem.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  projectIds: {
    type: Array,
    required: false,
    default: () => [],
  },
  selectedCostReportRowIds: {
    type: Array,
    required: false,
    default: () => [],
  },
  startOnLines: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['update', 'cancel'])

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { notifySuccess } = useNotify()
const { isUomTimeBased } = usePriceService()

const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const portalStore = usePortalStore()

const { costReportRowTypeEnum, invoiceStatusEnum, invoiceQuoteUpdateEnum, invoiceLineFieldTypeEnum } = accountingStore
const { documentRowTypeEnum } = webSiteStore

const isEdit = !!props.modelValue.id
const isDraft = !isEdit || props.modelValue.status === invoiceStatusEnum.DRAFT
const initialProjectIds = isEdit ? (props.modelValue.projects || []).map(project => project.id) : props.projectIds
const hasInitialProjects = initialProjectIds.length > 0
const { step, nextStep, prevStep, markAttempted, wasAttempted } = useStepper({ step: isEdit && !props.startOnLines ? 3 : hasInitialProjects ? 1 : 0 })

const onCancel = () => emit('cancel')
useNavigator(onCancel)

const today = dayjs().format('YYYY-MM-DD')
const invoiceDefaults = {
  invoice_number: '',
  status: invoiceStatusEnum.DRAFT,
  date: today,
  due_date: dueDateFrom(today, webSiteStore.getDefaultPaymentDays()),
  company: null,
  currency_ref: webSiteStore.getDefaultCurrency().id,
  print_template: webSiteStore.getDefaultInvoicePrintTemplate(),
  language: portalStore.selectedLanguage,
  payment_terms: '',
  payment_coordinates: '',
  budget_estimate_terms: '',
  intellectual_property_terms: '',
  notes: '',
}

const editItem = ref(isEdit
  ? Object.fromEntries(Object.entries(invoiceDefaults).map(([field, value]) => [field, props.modelValue[field] ?? value]))
  : { ...invoiceDefaults })
if (isEdit) editItem.value.due_date = props.modelValue.due_date ?? null
const { isUpdated, isUpdating } = useIsUpdated(editItem)
if (isEdit) useEscapeClose(isUpdated, onCancel)

const paymentDays = ref(isEdit
  ? (editItem.value.due_date ? dayjs(editItem.value.due_date).diff(editItem.value.date, 'day') : null)
  : webSiteStore.getDefaultPaymentDays())
const paymentDaysHasError = computed(() => paymentDays.value !== null && !isValidPaymentDays(paymentDays.value))
const paymentDaysRules = [() => !paymentDaysHasError.value || i18n.t('error.validation.minValue', { min: 1 })]

const onIssueDateChange = (date) => {
  editItem.value.date = date
  editItem.value.due_date = dueDateFrom(date, paymentDays.value)
}

const onPaymentDaysChange = (days) => {
  paymentDays.value = days === '' || days === null ? null : Number(days)
  if (!paymentDaysHasError.value) editItem.value.due_date = dueDateFrom(editItem.value.date, paymentDays.value)
}

const termTitles = ref({})

const invoiceNumberHasError = computed(() => !editItem.value.invoice_number?.trim())

const localizedStatuses = accountingStore.getLocalizedInvoiceStatuses()

const { getTypeChips } = useCostReportRowTypes()
const { getItemLabel: costReportLineLabel } = useCostReportRowLabels()

const showPartialWarning = ref(false)
const partialWarningAccepted = ref(false)
let resolvePartialWarning = null

const confirmPartialAsync = () => new Promise((resolve) => {
  resolvePartialWarning = resolve
  showPartialWarning.value = true
})

const onPartialWarningConfirm = () => {
  partialWarningAccepted.value = true
  resolvePartialWarning(true)
}

const showOverQuoteWarning = ref(false)
let resolveOverQuoteWarning = null

const overQuoteOptions = [
  { key: invoiceQuoteUpdateEnum.KEEP_QUOTE, label: i18n.t('common.invoiceKeepQuote'), icon: 'mdi-file-lock-outline', color: 'primary', standalone: true },
  { key: invoiceQuoteUpdateEnum.UPDATE_QUOTE, label: i18n.t('common.invoiceUpdateQuote'), icon: 'mdi-file-edit-outline', color: 'positive', standalone: true },
]

const chooseQuoteUpdateAsync = () => new Promise((resolve) => {
  resolveOverQuoteWarning = resolve
  showOverQuoteWarning.value = true
})

const confirmLineUpdateAsync = async (e, lines) => {
  const line = { ...e.oldData, ...e.newData }
  if (lines.isPartial(line) && !partialWarningAccepted.value && !await confirmPartialAsync()) return false
  if (!lines.isOverQuote(line)) {
    e.newData.update_quote = null
    return true
  }
  const choice = await chooseQuoteUpdateAsync()
  if (choice == null) return false
  e.newData.update_quote = choice === invoiceQuoteUpdateEnum.UPDATE_QUOTE
  return true
}

const buildLineGrid = (lines, initialSelectedKeys, stateId) => {
  const rows = ref([])
  const selectedKeys = ref([...initialSelectedKeys])
  const selectedLines = computed(() => rows.value.filter(row => selectedKeys.value.includes(row.id) && !lines.isInvoicedHere(row) && lines.hasQuantityToInvoice(row)))
  const invoicedHereKeys = computed(() => rows.value.filter(lines.isInvoicedHere).map(row => row.id))

  const gridsWithRestoredSelection = new WeakSet()

  const options = (columns) => {
    return {
      dataSource: rows.value,
      keyExpr: 'id',
      columns,
      showBorders: true,
      showColumnLines: true,
      allowColumnResizing: true,
      columnResizingMode: 'widget',
      allowColumnReordering: true,
      width: '100%',
      height: '40vh',
      paging: { enabled: false },
      sorting: { mode: 'multiple' },
      stateStoring: useDefaultStateStoring(stateId),
      columnChooser: useDefaultColumnChooser,
      toolbar: { items: [{ name: 'columnChooserButton', location: 'after', locateInMenu: 'never' }] },
      onContentReady: (e) => {
        if (gridsWithRestoredSelection.has(e.component)) return
        gridsWithRestoredSelection.add(e.component)
        e.component.selectRows(selectedKeys.value, false)
      },
      selection: { mode: 'multiple', showCheckBoxesMode: 'always' },
      editing: { mode: 'cell', allowUpdating: true, allowAdding: false, allowDeleting: false },
      onEditingStart: (e) => { e.cancel = !lines.isEditable(e.data, e.column?.name) },
      onEditorPreparing: (e) => {
        if (e.command === 'select' && e.parentType === 'dataRow' && lines.isInvoicedHere(e.row.data)) e.editorOptions.disabled = true
      },
      onSelectionChanged: (e) => {
        const deselectedInvoicedHereKeys = e.currentDeselectedRowKeys.filter(key => invoicedHereKeys.value.includes(key))
        if (deselectedInvoicedHereKeys.length) e.component.selectRows(deselectedInvoicedHereKeys, true)
        else selectedKeys.value = e.selectedRowKeys
      },
      onCellPrepared: (e) => lines.prepareEditableCell(e),
      onRowUpdated: (e) => e.component.selectRows([e.key], true),
      onRowUpdating: (e) => {
        e.cancel = confirmLineUpdateAsync(e, lines).then((isConfirmed) => {
          if (!isConfirmed) e.component.cancelEditData()
          return !isConfirmed
        })
      },
    }
  }

  const reload = (freshRows) => {
    const loadedLines = Object.fromEntries(rows.value.map(row => [row.id, row]))
    rows.value = freshRows.filter(lines.isInvoiceable).map(row => loadedLines[row.id] ?? lines.toLine(row))
    selectedKeys.value = [...new Set([...selectedKeys.value, ...invoicedHereKeys.value])]
  }

  const toggleSelected = (row) => {
    if (lines.isInvoicedHere(row)) return
    selectedKeys.value = selectedKeys.value.includes(row.id)
      ? selectedKeys.value.filter(key => key !== row.id)
      : [...selectedKeys.value, row.id]
  }

  return { rows, selectedKeys, selectedLines, options, reload, toggleSelected }
}

const currency = ref(null)

const budgetDocumentLines = useInvoiceLines({
  toPriceData: (row) => row,
  unitPriceFields: ['price', 'cost'],
  isEffortBased: (row) => isUomTimeBased(row.unit_of_measure_ref),
  isComment: (row) => row.document_row_type === documentRowTypeEnum.COMMENT,
  isQuoteLine: true,
  invoiceId: props.modelValue.id,
})
const costReportLines = useInvoiceLines({
  toPriceData: (row) => ({ ...row, price: row.cost, cost_valorization: accountingStore.costValorizationsEnum.PRICE, vat_type_ref: null }),
  fromPriceData: ({ price, ...changes }) => (price === undefined ? changes : { ...changes, cost: price }),
  unitPriceFields: ['cost'],
  isComment: (row) => row.type === costReportRowTypeEnum.COMMENT,
  invoiceId: props.modelValue.id,
})

const budgetDocumentGrid = buildLineGrid(budgetDocumentLines, [], 'invoice-wizard-budget-document-rows-grid-v3')
const costReportGrid = buildLineGrid(costReportLines, props.selectedCostReportRowIds, 'invoice-wizard-cost-report-rows-grid-v2')

const { selectedKeys: budgetDocumentSelectedKeys } = budgetDocumentGrid
const { selectedKeys: costReportSelectedKeys } = costReportGrid

const hasSelectedLines = computed(() => budgetDocumentGrid.selectedLines.value.length + costReportGrid.selectedLines.value.length > 0)

const budgetDocumentNames = ref({})
const budgetDocumentProjects = ref({})
const budgetDocumentsWithoutInvoiceableRows = ref([])
const projectNames = ref({})
const budgetDocumentListings = ref({})
const budgetDocumentGridOptions = ref(null)
const costReportGridOptions = ref(null)
const linesVersion = ref(0)

const readOnly = (columns) => columns.map(column => ({ ...column, allowEditing: false, validationRules: [] }))

const columnId = (column) => column.name ?? column.dataField ?? column.caption

const lineColumnAnchors = Object.freeze({
  billed_quantity: 'quantity',
  planned: 'unit_of_measure_ref',
})

const withInvoiceLineColumns = (columns, lines, defaultVisibleIds) => {
  const lineColumns = lines.buildColumns(currency.value?.code)
  const replacedColumnIds = ['invoice', ...lineColumns.map(columnId)]
  const baseColumns = readOnly(columns.filter(column => !replacedColumnIds.includes(columnId(column))))
    .map(column => ({ ...column, visible: defaultVisibleIds.includes(columnId(column)) }))
  const anchoredTo = (column) => lineColumns.filter(lineColumn => lineColumnAnchors[columnId(lineColumn)] === columnId(column))
  const withAnchored = (column) => [column, ...anchoredTo(column).flatMap(withAnchored)]
  const ordered = baseColumns.flatMap(withAnchored)
  return [...ordered, ...lineColumns.filter(lineColumn => !ordered.includes(lineColumn))]
}

const budgetDocumentProjectsColumn = useProjectsColumn({
  projectsOf: (row) => budgetDocumentProjects.value[row.budget_document] ?? [],
  calculateSortValue: (row) => (budgetDocumentProjects.value[row.budget_document] ?? []).map(project => project.name).join(', '),
  allowSorting: true,
  sortOrder: 'asc',
  sortIndex: 0,
})

const costReportProjectColumn = useProjectColumn({
  calculateSortValue: (row) => projectNames.value[row.project] ?? '',
  sortOrder: 'asc',
  sortIndex: 0,
})

const buildBudgetDocumentColumns = () => withInvoiceLineColumns([
  budgetDocumentProjectsColumn,
  {
    dataField: 'budget_document',
    caption: i18n.t('common.budgetDocument'),
    calculateDisplayValue: (row) => budgetDocumentNames.value[row.budget_document] || row.budget_document,
    calculateSortValue: (row) => budgetDocumentNames.value[row.budget_document] ?? '',
    alignment: 'left',
    sortOrder: 'asc',
    sortIndex: 1,
  },
  useRowNumberColumn({ sortIndex: 2 }),
  { dataField: 'listing_item_text', caption: i18n.t('common.listingItem') },
  { dataField: 'description', caption: i18n.t('common.description') },
  { dataField: 'quantity', caption: i18n.t('common.quantity'), dataType: 'number', format: '#0.##' },
  useUnitOfMeasureColumn(),
  usePriceColumn({ currency: currency.value }),
  useCostColumn({ currency: currency.value }),
  useVatTypeColumn(),
  useNotesColumn(),
], budgetDocumentLines, ['projects', 'budget_document', 'row_number', 'listing_item_text', 'description', 'quantity', 'unit_of_measure_ref', 'price', 'vat_type_ref'])

const buildCostReportColumns = () => withInvoiceLineColumns([
  costReportProjectColumn,
  ...useCostReportRowColumns(currency.value?.id).map(column => (column.dataField === 'date' ? { ...column, sortIndex: 1 } : column)),
], costReportLines, ['project', 'date', 'type', 'staff', 'reported_minutes', 'tag', 'unit_of_measure_ref', 'quantity', 'cost', 'notes'])

const budgetDocumentIdOf = (project) => {
  const budgetDocument = project?.budget_document
  return budgetDocument && typeof budgetDocument === 'object' ? budgetDocument.id : budgetDocument
}

const applyQuoteDefaults = (quote) => {
  if (!quote) return
  editItem.value.company = quote.company ?? null
  editItem.value.currency_ref = quote.currency_ref ?? editItem.value.currency_ref
  editItem.value.payment_terms = quote.payment_terms || ''
  editItem.value.payment_coordinates = quote.payment_coordinates || ''
  editItem.value.budget_estimate_terms = quote.budget_estimate_terms || ''
  editItem.value.intellectual_property_terms = quote.intellectual_property_terms || ''
}

const refreshTermTemplates = () => Promise.all([
  accountingStore.fetchPaymentTermTemplatesAsync({}, true),
  accountingStore.fetchBudgetEstimateTermTemplatesAsync({}, true),
  accountingStore.fetchIntellectualPropertyTermTemplatesAsync({}, true),
  accountingStore.fetchPaymentCoordinatesTemplatesAsync({}, true),
])

const selectedProjectIds = ref([...initialProjectIds])
const projectOptions = ref([])
const isLoadingLines = ref(false)

const filterProjects = (value, update) => update(() => {
  const query = value.toLowerCase()
  projectOptions.value = activeProjects().filter(project => project.name.toLowerCase().includes(query))
})

const loadLinesAsync = async () => {
  isLoadingLines.value = true
  try {
    const projects = await Promise.all(selectedProjectIds.value.map(id => planningStore.fetchProjectByIdAsync(id).then(({ data }) => data)))
    projectNames.value = Object.fromEntries(projects.map(project => [project.id, project.name]))

    const budgetDocumentIds = [...new Set(projects.map(budgetDocumentIdOf).filter(Boolean))]
    const [budgetDocumentRows, costReportRows, budgetDocuments] = await Promise.all([
      Promise.all(budgetDocumentIds.map(id => accountingStore.api.getBudgetDocumentRowsAsync({ budget_document: id, is_group_child: false }))),
      Promise.all(selectedProjectIds.value.map(id => accountingStore.api.getCostReportRowsAsync({ project: id }))),
      Promise.all(budgetDocumentIds.map(id => accountingStore.api.getBudgetDocumentByIdAsync(id).then(({ data }) => data))),
    ])
    budgetDocumentNames.value = Object.fromEntries(budgetDocuments.map(quote => [quote.id, quote.name]))
    budgetDocumentListings.value = Object.fromEntries(budgetDocuments.map(quote => [quote.id, quote.listing]))
    budgetDocumentProjects.value = Object.fromEntries(budgetDocuments.map(quote => [quote.id, quote.projects || []]))
    const invoiceableBudgetDocumentIds = new Set(
      budgetDocumentRows.flatMap(({ data }) => data)
        .filter(row => budgetDocumentLines.isInvoiceable(row) && !budgetDocumentLines.isComment(row))
        .map(row => row.budget_document),
    )
    budgetDocumentsWithoutInvoiceableRows.value = budgetDocuments.filter(quote => !invoiceableBudgetDocumentIds.has(quote.id)).map(quote => quote.name)
    if (!isEdit) applyQuoteDefaults(budgetDocuments[0])

    const currencyRef = isEdit ? editItem.value.currency_ref : projects[0]?.currency_ref ?? budgetDocuments[0]?.currency_ref
    currency.value = webSiteStore.currencies.find(c => c.id === currencyRef) || webSiteStore.getDefaultCurrency()

    budgetDocumentGrid.reload(budgetDocumentRows.flatMap(({ data }) => data))
    costReportGrid.reload(costReportRows.flatMap(({ data }) => data))
    budgetDocumentGridOptions.value = budgetDocumentGrid.options(buildBudgetDocumentColumns())
    costReportGridOptions.value = costReportGrid.options(buildCostReportColumns())
    linesVersion.value += 1
  } finally {
    isLoadingLines.value = false
  }
}

const editedLine = shallowRef(null)

const lineFields = [
  { field: 'billed_quantity', label: i18n.t('common.quantityToInvoice'), type: invoiceLineFieldTypeEnum.QUANTITY },
  { field: 'charge_percent', label: i18n.t('common.chargePercentage'), type: invoiceLineFieldTypeEnum.PERCENT },
  { field: 'discount_percent', label: i18n.t('common.discount'), type: invoiceLineFieldTypeEnum.PERCENT },
]

const lineTotal = (row) => useCurrencyColumnFormatter(row, 'total_no_vat', currency.value?.id)

const editLine = (row, lines, selectedKeys, label) => {
  editedLine.value = {
    row,
    lines,
    selectedKeys,
    title: label(row),
    summary: [
      { label: i18n.t('common.quantity'), value: fixedDecimal(row.original?.quantity) },
      { label: i18n.t('common.totalNoVat'), value: lineTotal(row) },
    ],
  }
}
const onQuoteLineEdit = (row) => editLine(row, budgetDocumentLines, budgetDocumentSelectedKeys, invoiceLineLabel)
const onCostReportLineEdit = (row) => editLine(row, costReportLines, costReportSelectedKeys, costReportLineLabel)

const onLineEdited = async (changes) => {
  const { row, lines, selectedKeys } = editedLine.value
  editedLine.value = null
  const change = { oldData: row, newData: { ...changes } }
  if (!await confirmLineUpdateAsync(change, lines)) return
  Object.assign(row, change.newData)
  if (!selectedKeys.value.includes(row.id)) selectedKeys.value = [...selectedKeys.value, row.id]
}

const showListingFirst = ref(webSiteStore.getDefaultShowListingFirst())
const budgetDocumentRowLabels = useBudgetDocumentRowLabels({
  showListingFirst,
  hasListing: (row) => !!budgetDocumentListings.value[row.budget_document],
})

const budgetDocumentLineGroups = computed(() => Object.entries(budgetDocumentNames.value).map(([id, title]) => ({
  id,
  title,
  rows: budgetDocumentGrid.rows.value.filter(row => String(row.budget_document) === id),
})).filter(group => group.rows.length > 0))

const costReportLineGroups = computed(() => Object.entries(projectNames.value).flatMap(([projectId, projectName]) => [
  { type: costReportRowTypeEnum.TIME, icon: 'mdi-clock-outline', title: i18n.t('common.costReportTime') },
  { type: costReportRowTypeEnum.EXPENSE, icon: 'mdi-currency-eur', title: i18n.t('common.expense') },
].map(({ type, icon, title }) => ({
  id: `${projectId}-${type}`,
  icon,
  title: Object.keys(projectNames.value).length > 1 ? `${projectName} · ${title}` : title,
  rows: costReportGrid.rows.value.filter(row => String(row.project) === projectId && row.type === type),
}))).filter(group => group.rows.length > 0))

const isMissingLines = computed(() => !isEdit && wasAttempted(2) && !hasSelectedLines.value)

const onLinesConfirmed = () => {
  markAttempted(2)
  if (!isMissingLines.value) nextStep()
}

const onProjectsConfirmed = async () => {
  await loadLinesAsync()
  nextStep()
}

const activeProjects = () => planningStore.projects.filter(project => !project.archived || selectedProjectIds.value.includes(project.id))

const loadLinesDataAsync = () => Promise.all([
  ...(isMobile.value ? [budgetDocumentRowLabels.loadListingItemsAsync()] : []),
  planningStore.fetchProjectsAsync({}, true).then(() => { projectOptions.value = activeProjects() }),
  ...(hasInitialProjects ? [loadLinesAsync()] : []),
  planningStore.fetchStaffsAsync({}, true),
  accountingStore.fetchCostReportRowTagsAsync({}, true),
  accountingStore.fetchCompanyDataAsync({}, true),
])

onMounted(async () => {
  await Promise.all([
    planningStore.fetchCompaniesAsync({ show_archived: true }, true),
    refreshTermTemplates(),
    ...(isDraft ? [loadLinesDataAsync()] : []),
    ...(isEdit ? [] : [accountingStore.fetchNextInvoiceNumberAsync().then((number) => { editItem.value.invoice_number ||= number })]),
  ])
})

const validateStep = (stepName, hasError) => {
  markAttempted(stepName)
  if (hasError.value) step.value = stepName
  return !hasError.value
}
const validateInvoiceData = () => validateStep(3, invoiceNumberHasError)
const validateInvoiceDates = () => validateStep(4, paymentDaysHasError)
const validateInvoice = () => validateInvoiceData() && validateInvoiceDates()

const invoicePayload = () => ({ ...editItem.value, invoice_number: editItem.value.invoice_number.trim() })

const selectedLinesPayload = () => ({
  budget_document_rows: budgetDocumentGrid.selectedLines.value.map(budgetDocumentLines.toPayload),
  cost_report_rows: costReportGrid.selectedLines.value.map(costReportLines.toPayload),
})

const onConfirm = async () => {
  const { data: invoice } = await accountingStore.upsertInvoiceAsync({ ...invoicePayload(), ...selectedLinesPayload() })
  notifySuccess(i18n.t('info.createdSuccessfully'))
  emit('update', invoice.id)
  await router.push({ path: menuRoutes[menuRouteEnum.ACCOUNTING_INVOICES], query: { highlight: invoice.id } })
}

const onUpdate = async () => {
  isUpdating.value = true
  try {
    await accountingStore.upsertInvoiceAsync({ id: props.modelValue.id, ...invoicePayload(), ...(isDraft ? selectedLinesPayload() : {}) })
    notifySuccess(i18n.t('info.updatedSuccessfully'))
    emit('update', props.modelValue.id)
  } finally {
    isUpdating.value = false
  }
}
</script>

<template>
  <q-card class="dialog-card-flex">
    <confirm-dialog
      v-model="showPartialWarning"
      :message="i18n.t('warning.invoicePartialQuantityNewRecord')"
      icon="mdi-alert"
      icon-color="warning"
      icon-size="md"
      @confirm="onPartialWarningConfirm"
      @cancel="resolvePartialWarning(false)"
    />
    <delete-options-dialog
      v-model="showOverQuoteWarning"
      :title="i18n.t('common.invoiceOverQuote')"
      :message="i18n.t('warning.invoiceOverQuote')"
      :options="overQuoteOptions"
      @select="(choice) => resolveOverQuoteWarning(choice)"
      @cancel="resolveOverQuoteWarning(null)"
    />
    <q-dialog :model-value="editedLine !== null" maximized @hide="editedLine = null">
      <invoice-line-edit
        v-if="editedLine !== null"
        :title="editedLine.title"
        :row="editedLine.row"
        :fields="lineFields"
        :summary="editedLine.summary"
        :apply="editedLine.lines.setLineValue"
        @update="onLineEdited"
        @cancel="editedLine = null"
      />
    </q-dialog>
    <navigate-back
      :text="isEdit ? `${i18n.t('common.edit')} ${i18n.t('common.invoice').toLocaleLowerCase()}` : i18n.t('common.generateInvoice')"
      @cancel="onCancel"
    />
    <q-card-section class="scroll col q-pa-none dialog-card-content">
      <q-card-section class="q-px-sm">
        <q-stepper
          v-model="step"
          class="q-py-none"
          vertical
        >
          <q-step
            v-if="isDraft"
            :name="0"
            :title="i18n.t('common.project', 2)"
            icon="mdi-briefcase-outline"
            :done="isEdit || step > 0"
            :header-nav="isEdit"
          >
            <q-select
              v-model="selectedProjectIds"
              :label="i18n.t('common.project', 2)"
              :options="projectOptions"
              option-value="id"
              option-label="name"
              emit-value
              map-options
              multiple
              use-chips
              use-input
              :input-debounce="webSiteStore.getInputDebounce()"
              @filter="filterProjects"
            />
            <stepper-navigation :continue="onProjectsConfirmed" :disable="!selectedProjectIds.length || isLoadingLines" />
          </q-step>

          <q-step
            v-if="isDraft"
            :name="1"
            :title="i18n.t('common.budgetDocumentRow', 2)"
            icon="mdi-file-document-outline"
            :done="isEdit || step > 1"
            :header-nav="isEdit"
          >
            <template v-if="isMobile">
              <div v-for="group in budgetDocumentLineGroups" :key="group.id">
                <div class="group-row">
                  <q-item-label style="font-weight: bolder">
                    <q-icon name="mdi-file-document-outline" class="q-mr-xs" />{{ group.title }}
                  </q-item-label>
                </div>
                <budget-document-row-list-item
                  v-for="row in group.rows"
                  :key="row.id"
                  :row="row"
                  :labels="budgetDocumentRowLabels"
                  :currency-ref="editItem.currency_ref"
                  :show-listing-first="showListingFirst"
                  :class="{ 'selected-row': budgetDocumentSelectedKeys.includes(row.id) }"
                  hide-avatar
                  clickable
                  :icon="budgetDocumentLines.isEditable(row) ? 'mdi-pencil' : ''"
                  @click="budgetDocumentGrid.toggleSelected(row)"
                  @icon-click="onQuoteLineEdit(row)"
                />
              </div>
            </template>
            <dx-data-grid v-else-if="budgetDocumentGridOptions" :key="linesVersion" v-bind="budgetDocumentGridOptions" />
            <info-box
              v-if="budgetDocumentsWithoutInvoiceableRows.length"
              class="q-mt-sm"
              :tone="infoBoxToneEnum.WARNING"
              :message="i18n.t('warning.invoiceQuotesWithoutInvoiceableRows', { quotes: budgetDocumentsWithoutInvoiceableRows.join(', ') })"
            />
            <info-box :id="infoBoxIdEnum.INVOICE_LINES_EDITABLE" class="q-mt-sm" :message="i18n.t('info.invoiceLinesEditable')" />
            <stepper-navigation :back="prevStep" :continue="nextStep" />
          </q-step>

          <q-step
            v-if="isDraft"
            :name="2"
            :title="i18n.t('common.costReportRow', 2)"
            icon="mdi-format-list-checks"
            :done="isEdit || step > 2"
            :header-nav="isEdit"
            :error="isMissingLines"
            error-color="negative"
            error-icon="mdi-alert-circle"
          >
            <template v-if="isMobile">
              <div v-for="group in costReportLineGroups" :key="group.id">
                <div class="group-row cost-report-group-row">
                  <q-item-label style="font-weight: bolder;">
                    <q-icon :name="group.icon" class="q-mr-xs" />{{ group.title }}
                  </q-item-label>
                </div>
                <cost-report-row-list-item
                  v-for="row in group.rows"
                  :key="row.id"
                  :row="row"
                  :currency-ref="editItem.currency_ref"
                  :class="{ 'selected-row': costReportGrid.selectedKeys.value.includes(row.id) }"
                  clickable
                  :icon="costReportLines.isEditable(row) ? 'mdi-pencil' : ''"
                  @click="costReportGrid.toggleSelected(row)"
                  @icon-click="onCostReportLineEdit(row)"
                />
              </div>
            </template>
            <dx-data-grid v-else-if="costReportGridOptions" :key="linesVersion" v-bind="costReportGridOptions">
              <template #type-cell-template="{ data: templateOptions }">
                <chip-template :values="getTypeChips(templateOptions.data.type)" />
              </template>
            </dx-data-grid>
            <info-box
              v-if="isMissingLines"
              class="q-mt-sm"
              :tone="infoBoxToneEnum.WARNING"
              :message="i18n.t('warning.invoiceSelectAtLeastOneRow')"
            />
            <info-box :id="infoBoxIdEnum.INVOICE_LINES_EDITABLE" class="q-mt-sm" :message="i18n.t('info.invoiceLinesEditable')" />
            <stepper-navigation :back="prevStep" :continue="onLinesConfirmed" />
          </q-step>

          <q-step
            :name="3"
            :title="i18n.t('common.invoiceData')"
            icon="mdi-file-document-outline"
            :done="isEdit || step > 3"
            :header-nav="isEdit || step > 3"
            :error="wasAttempted(3) && invoiceNumberHasError"
            error-color="negative"
            error-icon="mdi-alert-circle"
          >
            <info-box
              v-if="!isDraft"
              class="q-mb-sm"
              :message="i18n.t('info.invoiceNotDraftReadonly')"
            />
            <div class="row q-col-gutter-sm">
              <q-input
                v-model="editItem.invoice_number"
                :class="isMobile ? 'col-12' : 'col-4'"
                :label="i18n.t('common.invoiceNumber')"
                :readonly="!isDraft"
                autofocus
                :rules="[val => !!(val && val.trim()) || i18n.t('common.required')]"
              />
              <q-select
                v-model="editItem.status"
                :class="isMobile ? 'col-12' : 'col-4'"
                :label="i18n.t('common.status')"
                :options="localizedStatuses"
                option-value="id"
                option-label="name"
                emit-value
                map-options
              />
              <currency-select
                v-model="editItem.currency_ref"
                :class="isMobile ? 'col-12' : 'col-4'"
                :readonly="isEdit"
              />
              <print-template-select
                v-model="editItem.print_template"
                :document-type="accountingStore.printDocumentTypeEnum.INVOICE"
                :readonly="!isDraft"
                :class="isMobile ? 'col-12' : 'col-8'"
              />
              <document-language-select
                v-model="editItem.language"
                :class="isMobile ? 'col-12' : 'col-4'"
              />
              <form-section-title :label="i18n.t('common.customer')" />
              <company-combobox
                v-model="editItem.company"
                class="col-12"
                :readonly="!isDraft"
              />
            </div>
            <stepper-navigation
              :back="isDraft ? prevStep : null"
              :continue="() => { if (validateInvoiceData()) nextStep() }"
            />
          </q-step>

          <q-step
            :name="4"
            :title="i18n.t('common.date', 2)"
            icon="mdi-calendar-clock"
            :done="isEdit || step > 4"
            :header-nav="isEdit || step > 4"
            :error="wasAttempted(4) && paymentDaysHasError"
            error-color="negative"
            error-icon="mdi-alert-circle"
          >
            <div class="row q-col-gutter-sm">
              <q-input
                :model-value="editItem.date"
                type="date"
                :class="isMobile ? 'col-12' : 'col-4'"
                :label="i18n.t('common.emissionDate')"
                :readonly="!isDraft"
                @update:model-value="onIssueDateChange"
              />
              <q-input
                :model-value="paymentDays"
                type="number"
                min="1"
                :class="isMobile ? 'col-12' : 'col-4'"
                :label="i18n.t('common.paymentDays')"
                :rules="paymentDaysRules"
                @update:model-value="onPaymentDaysChange"
              >
                <template #append>
                  <q-btn
                    flat
                    round
                    dense
                    icon="mdi-menu-down"
                  >
                    <q-menu auto-close>
                      <q-list dense>
                        <q-item
                          v-for="days in webSiteStore.getPaymentDaysOptions()"
                          :key="days"
                          clickable
                          :active="days === paymentDays"
                          @click="onPaymentDaysChange(days)"
                        >
                          <q-item-section>{{ paymentDaysLabel(days) }}</q-item-section>
                        </q-item>
                      </q-list>
                    </q-menu>
                  </q-btn>
                </template>
              </q-input>
              <q-input
                :model-value="formatShortDate(editItem.due_date)"
                :class="isMobile ? 'col-12' : 'col-4'"
                :label="i18n.t('common.dueDate')"
                readonly
              />
            </div>
            <stepper-navigation :back="prevStep" :continue="() => { if (validateInvoiceDates()) nextStep() }" />
          </q-step>

          <q-step
            :name="5"
            :title="i18n.t('common.payment', 2)"
            icon="mdi-currency-usd"
            :done="isEdit || step > 5"
            :header-nav="isEdit || step > 5"
          >
            <div class="row q-col-gutter-sm">
              <document-term-template-select
                v-model:template-title="termTitles[accountingStore.documentTermTemplateTypeEnum.PAYMENT_TERMS]"
                v-model="editItem.payment_terms"
                :label="i18n.t('common.paymentTerms')"
                :template-label="i18n.t('common.paymentTermTemplate')"
                :term-type="accountingStore.documentTermTemplateTypeEnum.PAYMENT_TERMS"
                :templates="accountingStore.paymentTermTemplates"
                class="col-12"
                @refresh="refreshTermTemplates"
              />
              <document-term-template-select
                v-model:template-title="termTitles[accountingStore.documentTermTemplateTypeEnum.PAYMENT_COORDINATES]"
                v-model="editItem.payment_coordinates"
                :label="i18n.t('common.paymentCoordinates')"
                :template-label="i18n.t('common.paymentCoordinatesTemplate')"
                :term-type="accountingStore.documentTermTemplateTypeEnum.PAYMENT_COORDINATES"
                :templates="accountingStore.paymentCoordinatesTemplates"
                class="col-12"
                @refresh="refreshTermTemplates"
              />
            </div>
            <stepper-navigation :back="prevStep" :continue="nextStep" />
          </q-step>

          <q-step
            :name="6"
            :title="i18n.t('common.additionalInfo', 2)"
            icon="mdi-information-outline"
            :done="isEdit || step > 6"
            :header-nav="isEdit || step > 6"
          >
            <div class="row q-col-gutter-sm">
              <document-term-template-select
                v-model:template-title="termTitles[accountingStore.documentTermTemplateTypeEnum.BUDGET_ESTIMATE_TERMS]"
                v-model="editItem.budget_estimate_terms"
                :label="i18n.t('common.budgetEstimateTerms')"
                :template-label="i18n.t('common.budgetEstimateTermTemplate')"
                :term-type="accountingStore.documentTermTemplateTypeEnum.BUDGET_ESTIMATE_TERMS"
                :templates="accountingStore.budgetEstimateTermTemplates"
                class="col-12"
                @refresh="refreshTermTemplates"
              />
              <document-term-template-select
                v-model:template-title="termTitles[accountingStore.documentTermTemplateTypeEnum.INTELLECTUAL_PROPERTY_TERMS]"
                v-model="editItem.intellectual_property_terms"
                :label="i18n.t('common.intellectualPropertyTerms')"
                :template-label="i18n.t('common.intellectualPropertyTermTemplate')"
                :term-type="accountingStore.documentTermTemplateTypeEnum.INTELLECTUAL_PROPERTY_TERMS"
                :templates="accountingStore.intellectualPropertyTermTemplates"
                class="col-12"
                @refresh="refreshTermTemplates"
              />
              <q-input
                v-model="editItem.notes"
                autogrow
                class="col-12"
                :label="i18n.t('common.note', 2)"
              />
            </div>
            <stepper-navigation
              :back="prevStep"
              :finish="isEdit ? null : () => { if (validateInvoice()) onConfirm() }"
              :finish-label="i18n.t('common.generateInvoice')"
            />
          </q-step>
        </q-stepper>
      </q-card-section>
    </q-card-section>
    <q-card-actions v-if="isEdit && (isUpdated || hasSelectedLines)" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn"
        color="positive"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="() => { if (validateInvoice()) onUpdate() }"
      />
    </q-card-actions>
  </q-card>
</template>

<style scoped lang="scss">
.group-row {
  padding-top: .5rem;
  padding-bottom: .5rem;
  display: flex;
  align-items: center;
}

.cost-report-group-row {
  padding-top: 1rem;
  border-bottom: 1px solid var(--row-card-border-color);
  color: $primary;
}
</style>
